import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, BookOpen, CalendarDays, Check, Compass, Gift, Settings, Sparkles, Users, X } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { dismissWelcome, setWelcomeHidden, shouldShowWelcome } from '../utils/welcomeTour';

const steps = [
  {
    title: 'Bienvenido a tu brújula del tiempo',
    description: 'Comienza en Energía diaria: selecciona una fecha en el calendario y consulta tus números y mensajes para trabajo, amor, bienestar y espiritualidad.',
    hint: 'Puedes volver al día actual con el botón HOY.',
    icon: CalendarDays,
    kind: 'calendar'
  },
  {
    title: 'Conoce tu mapa personal',
    description: 'En Etapa de vida y Mi Pináculo puedes consultar la información numerológica de tu perfil. En Quién soy encontrarás tu horizontal numerológica.',
    hint: 'Estas secciones usan la fecha de nacimiento del perfil seleccionado.',
    icon: Compass,
    kind: 'map'
  },
  {
    title: 'Explora tus perfiles',
    description: 'El selector del encabezado te permite cambiar entre tu perfil y los perfiles Ninja que hayas agregado. En Numerología de pareja encontrarás tu regalo: un reporte de compatibilidad generado por IA.',
    hint: 'La cantidad de perfiles Ninja disponibles depende de tu membresía.',
    icon: Users,
    kind: 'profiles'
  },
  {
    title: 'Encuentra tus agendas',
    description: 'Abre Agenda 2026 para consultar su sección de descarga. Agenda 2027 está señalada como Próximamente y todavía no se puede abrir.',
    hint: 'La descarga de Agenda 2026 tiene sus propias condiciones de acceso.',
    icon: BookOpen,
    kind: 'agenda'
  },
  {
    title: 'Tu cuenta, siempre a mano',
    description: 'Desde Mi Cuenta puedes consultar tu acceso. Si estás en la prueba de 5 días, verás su tiempo restante en la franja superior y podrás consultar tu beneficio.',
    hint: 'Puedes volver a abrir este recorrido desde el botón Ver beneficios del encabezado.',
    icon: Settings,
    kind: 'account'
  }
];

function TourPreview({ kind, reducedMotion }) {
  const animation = reducedMotion ? {} : { y: [0, -4, 0] };
  const transition = { duration: 3, repeat: Infinity, ease: 'easeInOut' };
  return <div aria-hidden="true" className="rounded-2xl bg-morado/5 border border-morado/10 p-3 sm:p-6 min-h-[144px] sm:min-h-[172px] flex items-center justify-center">
    {kind === 'calendar' && <div className="w-full max-w-sm">
      <div className="flex items-center justify-between mb-3 text-xs font-semibold text-morado">
        <span className="flex items-center gap-2"><CalendarDays size={16} /> Tu calendario</span><span className="rounded-md bg-white px-2 py-1">HOY</span>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, index) => <span key={index} className="text-center text-[10px] text-gray-500">{day}</span>)}
        {Array.from({ length: 7 }, (_, index) => <motion.span key={index} animate={index === 3 ? animation : {}} transition={transition} className={`rounded-lg py-2 sm:py-3 text-center text-sm font-semibold ${index === 3 ? 'bg-morado text-white shadow-md' : 'bg-white text-morado'}`}>{index + 1}</motion.span>)}
      </div>
      <p className="mt-3 text-center text-[10px] text-gray-400">Vista de ejemplo</p>
    </div>}
    {kind === 'map' && <div className="grid grid-cols-3 gap-2 sm:gap-4 w-full">
      {[['Etapa de vida', Sparkles], ['Mi Pináculo', Compass], ['Quién soy', Users]].map(([label, Icon], index) => <motion.div key={label} animate={animation} transition={{ ...transition, delay: index * 0.25 }} className="rounded-xl bg-white px-2 py-5 text-center shadow-sm">
        <Icon className="w-6 h-6 mx-auto text-morado mb-3" /><p className="text-xs font-semibold text-morado">{label}</p>
      </motion.div>)}
    </div>}
    {kind === 'profiles' && <div className="w-full max-w-xs space-y-3">
      <motion.div animate={animation} transition={transition} className="flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm"><span className="rounded-full bg-morado p-2 text-white"><Users size={20} /></span><div><p className="text-sm font-semibold text-morado">Tu perfil</p><p className="text-xs text-gray-500">Selector de perfiles</p></div><Check className="ml-auto text-morado" size={18} /></motion.div>
      <div className="flex items-center gap-3 rounded-xl border border-[#d4af37]/40 bg-white p-3"><Gift size={20} className="text-morado" /><span className="text-sm font-semibold text-morado">Numerología de pareja</span><span className="ml-auto text-xs text-gray-500">Regalo</span></div>
    </div>}
    {kind === 'agenda' && <div className="flex items-center justify-center gap-3 sm:gap-6">
      {[2026, 2027].map(year => <motion.div key={year} animate={year === 2026 ? animation : {}} transition={transition} className="relative rounded-r-xl rounded-l-sm border-l-4 border-morado bg-white px-4 py-5 shadow-md text-center">
        <BookOpen className="w-6 h-6 mx-auto text-morado mb-2" /><p className="text-[10px] uppercase tracking-widest text-gray-500">Agenda</p><p className="text-2xl font-bold text-morado">{year}</p>
        {year === 2027 && <span className="mt-2 block rounded-full bg-[#d4af37]/25 px-2 py-1 text-[10px] text-morado">Próximamente</span>}
      </motion.div>)}
    </div>}
    {kind === 'account' && <motion.div animate={animation} transition={transition} className="w-full max-w-xs rounded-2xl bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3"><span className="rounded-xl bg-[#d4af37]/20 p-3"><Settings className="text-morado" size={24} /></span><div><p className="text-sm font-semibold text-morado">Mi Cuenta</p><p className="text-xs text-gray-500 mt-1">Tu acceso y membresía</p></div></div>
      <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-500"><Sparkles size={14} className="text-morado" /> Vuelve a abrirla desde Ver beneficios.</div>
    </motion.div>}
  </div>;
}

export default function WelcomeTour({ userId, requestVersion = 0 }) {
  const [open, setOpen] = useState(() => shouldShowWelcome(userId));
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const current = steps[step];
  const Icon = current.icon;

  useEffect(() => {
    if (requestVersion > 0) { setStep(0); setDirection(1); setOpen(true); }
  }, [requestVersion]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open) { if (dialog.open) dialog.close(); return; }
    dialog.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (dialog.open) dialog.close();
    };
  }, [open]);

  useEffect(() => { if (open) headingRef.current?.focus(); }, [open, step]);

  const dismiss = () => {
    dismissWelcome(userId);
    setOpen(false);
  };
  const hideForever = () => {
    setWelcomeHidden(userId, true);
    dismiss();
  };
  const changeStep = next => { setDirection(next > step ? 1 : -1); setStep(next); };
  const keepFocusInDialog = event => {
    if (event.key !== 'Tab') return;
    const buttons = [...dialogRef.current.querySelectorAll('button:not(:disabled)')];
    const first = buttons[0];
    const last = buttons.at(-1);
    if (event.shiftKey && (document.activeElement === first || document.activeElement === headingRef.current)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  return <dialog ref={dialogRef} aria-labelledby="welcome-tour-title" aria-describedby="welcome-tour-description" onKeyDown={keepFocusInDialog} onCancel={event => { event.preventDefault(); dismiss(); }} className="m-auto max-w-2xl max-h-[90dvh] overflow-y-auto rounded-3xl border-0 p-0 bg-white text-gray-800 shadow-2xl backdrop:bg-[#241020]/60 backdrop:backdrop-blur-sm" style={{ width: 'min(42rem, calc(100% - 2rem))' }}>
    {open && <motion.div initial={reducedMotion ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reducedMotion ? 0 : 0.25 }} className="p-5 sm:p-8">
      <div className="flex items-center justify-between gap-4 mb-4 sm:mb-6">
        <BrandLogo className="w-40 sm:w-48" />
        <button type="button" onClick={dismiss} aria-label="Cerrar bienvenida" className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-morado focus-visible:outline-2 focus-visible:outline-morado"><X size={20} /></button>
      </div>
      <div className="flex items-center gap-2 text-morado mb-2 sm:mb-3"><Icon size={18} aria-hidden="true" /><p className="text-xs font-semibold uppercase tracking-widest">Descubre Numerathum · {step + 1} de {steps.length}</p></div>
      <h2 ref={headingRef} tabIndex={-1} id="welcome-tour-title" className="text-2xl sm:text-3xl font-bold text-morado outline-none">{current.title}</h2>
      <p id="welcome-tour-description" className="mt-3 text-sm sm:text-base leading-relaxed text-gray-600 min-h-[84px]">{current.description}</p>
      <div className="mt-4 sm:mt-5 overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={step} initial={reducedMotion ? false : { opacity: 0, x: direction * 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: reducedMotion ? 1 : 0, x: reducedMotion ? 0 : direction * -24 }} transition={{ duration: reducedMotion ? 0 : 0.18 }}>
            <TourPreview kind={current.kind} reducedMotion={reducedMotion} />
          </motion.div>
        </AnimatePresence>
      </div>
      <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 leading-relaxed">{current.hint}</p>
      <div className="flex items-center justify-between mt-4 sm:mt-6 gap-3">
        <div className="flex items-center gap-1.5" aria-label={`Paso ${step + 1} de ${steps.length}`}>
          {steps.map((item, index) => <span key={item.kind} className={`h-1.5 rounded-full transition-all ${index === step ? 'w-6 bg-morado' : 'w-1.5 bg-morado/15'}`} />)}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
          <button type="button" onClick={hideForever} className="text-xs font-medium text-gray-500 underline-offset-2 hover:text-morado hover:underline">No volver a mostrar</button>
          <button type="button" onClick={dismiss} className="text-xs font-medium text-gray-500 hover:text-morado">Omitir recorrido</button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-gray-100 mt-3 pt-3 sm:mt-5 sm:pt-5">
        <button type="button" disabled={step === 0} onClick={() => changeStep(step - 1)} className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-morado hover:bg-morado/5 disabled:opacity-30 disabled:cursor-not-allowed"><ArrowLeft size={16} aria-hidden="true" />Anterior</button>
        <button type="button" onClick={() => step === steps.length - 1 ? dismiss() : changeStep(step + 1)} className="inline-flex items-center gap-2 rounded-xl bg-morado px-5 py-2.5 text-sm font-semibold text-white hover:bg-morado/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-morado">{step === steps.length - 1 ? 'Comenzar a explorar' : 'Siguiente'}<ArrowRight size={16} aria-hidden="true" /></button>
      </div>
    </motion.div>}
  </dialog>;
}
